"use client";

import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, CheckCircle, ChevronDown } from 'lucide-react';
import { submitEnquiry } from '../utils/formSubmission';
import CallButton from "./CallButton";

const Contact = () => {
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(e.target);
        const data = {
            name: formData.get('name'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            subject: formData.get('subject') || "Contact Form Submission",
            message: formData.get('message'),
        };

        try {
            await submitEnquiry(data);
            setSubmitted(true);
            e.target.reset();
            setTimeout(() => setSubmitted(false), 5000);
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="py-15 md:py-20 bg-white">
            <div className="container mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

                    {/* Left: Contact Info */}
                    <div className="space-y-10 md:space-y-12">
                        <div className="space-y-6">
                            {/* Added Icon beside Get in Touch */}
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 text-brand text-[12px] font-black  tracking-widest">
                                <MessageSquare size={14} strokeWidth={3} />
                                Get in Touch
                            </span>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 tracking-tighter leading-[1.1]">
                                Ready to fuel <br className="hidden md:block" />
                                <span className="text-brand">your growth?</span>
                            </h2>
                            <p className="text-slate-700 font-medium text-lg max-w-md">
                                Reach out to our automotive experts today for a personalized demo of our DMS ecosystem.
                            </p>
                        </div>

                        <div className="space-y-6 md:space-y-8">
                            <div className="flex gap-5 md:gap-6 items-start group">
                                <div className="w-12 h-12 shrink-0 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-brand shadow-sm group-hover:bg-brand group-hover:text-white transition-all duration-300">
                                    <MapPin size={22} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Headquarters</h3>
                                    <p className="text-slate-700 font-medium leading-relaxed">
                                        Emsys Solution Pvt. Ltd., Opp. Vasant Market, <br />
                                        Vatsalya, Vrundavan Colony, Canada Corner, <br />
                                        Nashik, Maharashtra 422002
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-5 md:gap-6 items-start group">
                                <div className="w-12 h-12 shrink-0 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-brand shadow-sm group-hover:bg-brand group-hover:text-white transition-all duration-300">
                                    <Phone size={22} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Call Us</h3>
                                    <CallButton href="tel:+919011060932" location="contact" className="text-slate-700 font-medium">+91 9011060932</CallButton>
                                    <p className="text-slate-600 text-xs font-black  tracking-tighter">Mon - Sat, 10 am - 7 pm</p>
                                </div>
                            </div>

                            <div className="flex gap-5 md:gap-6 items-start group">
                                <div className="w-12 h-12 shrink-0 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-brand shadow-sm group-hover:bg-brand group-hover:text-white transition-all duration-300">
                                    <Mail size={22} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Email Us</h3>
                                    <p className="text-slate-700 font-medium">sales@emsys.co.in</p>
                                    <p className="text-slate-600 text-xs font-black  tracking-tighter">Response within 24hrs</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Contact Form */}
                    <div className="relative w-full">
                        <div className="bg-white p-6 md:p-10 lg:p-12 rounded-[2.5rem] md:rounded-[3rem] border border-brand/20 shadow-2xl shadow-slate-200/60">
                            {submitted ? (
                                <div className="py-16 flex flex-col items-center text-center space-y-4">
                                    <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center animate-bounce">
                                        <CheckCircle size={40} />
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-900">Message Sent!</h3>
                                    <p className="text-slate-700 font-medium">Thank you. Our team will contact you shortly.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 gap-5">
                                        {/* Name Field */}
                                        <div className="space-y-1.5">
                                            <label htmlFor="contact-name" className="text-[10px] font-black  tracking-[0.15em] text-slate-600 ml-2">Full Name</label>
                                            <input id="contact-name" name="name" required type="text" placeholder="John Doe"
                                                className="w-full px-6 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold placeholder:text-slate-300" />
                                        </div>

                                        {/* Email & Phone Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div className="space-y-1.5">
                                                <label htmlFor="contact-email" className="text-[10px] font-black  tracking-[0.15em] text-slate-600 ml-2">Email Address</label>
                                                <input id="contact-email" name="email" required type="email" placeholder="john@dealership.com"
                                                    className="w-full px-6 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold placeholder:text-slate-300" />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="contact-phone" className="text-[10px] font-black  tracking-[0.15em] text-slate-600 ml-2">Phone Number</label>
                                                <input id="contact-phone" name="phone" required type="tel" placeholder="+91 00000 00000"
                                                    className="w-full px-6 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold placeholder:text-slate-300" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5 relative">
                                        <label htmlFor="contact-subject" className="text-[10px] font-black  tracking-[0.15em] text-slate-600 ml-2">Interested In</label>
                                        <div className="relative">
                                            <select id="contact-subject" name="subject" className="w-full px-6 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold appearance-none cursor-pointer">
                                                <option>Autonet DMS</option>
                                                <option>AutoNext CRM</option>
                                                <option>Web Based DMS</option>
                                                <option>Custom Software</option>
                                            </select>
                                            <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" size={18} />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="contact-message" className="text-[10px] font-black  tracking-[0.15em] text-slate-600 ml-2">Your Message</label>
                                        <textarea id="contact-message" name="message" rows="3" placeholder="Tell us about your dealership requirements..."
                                            className="w-full px-6 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold resize-none placeholder:text-slate-300"></textarea>
                                    </div>

                                    <button disabled={isSubmitting} type="submit" className="w-full py-5 bg-brand text-white rounded-xl font-black text-lg flex items-center justify-center gap-3 hover:bg-slate-900 hover:shadow-xl hover:shadow-brand/20 transition-all active:scale-[0.97] mt-2 disabled:opacity-50">
                                        {isSubmitting ? 'Sending...' : 'Send Message'}
                                        <Send size={20} strokeWidth={2.5} />
                                    </button>
                                </form>
                            )}
                        </div>

                        {/* Mobile Responsive Decoration */}
                        <div className="absolute -bottom-6 -right-6 w-32 h-32 md:w-64 md:h-64 bg-brand/5 rounded-full blur-3xl -z-10" />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;